import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  Video, 
  BookOpen, 
  Music, 
  Image, 
  ExternalLink, 
  AlertCircle,
  Eye,
  FileDown
} from 'lucide-react';
import { authService } from '../authService'
import { AUTH_CONFIG } from '../config'

// Helper function to create URL-friendly names
const createSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .trim();                   // Trim leading/trailing spaces
};

const ContentItemCard = ({ item, isOpen, onClick, onCustomAction }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const navigate = useNavigate();
  
  // Determine icon based on item type
  const getIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'video': return <Video size={18} />;
      case 'pdf': return <FileText size={18} />;
      case 'ebook': return <BookOpen size={18} />;
      case 'audio': return <Music size={18} />;
      case 'image': return <Image size={18} />;
      default: return <FileText size={18} />;
    }
  };

  // Truncate description if needed
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Handle the action button click
  const handleItemAction = (e) => {
    e.stopPropagation();
    
    const itemType = item.type?.toLowerCase();
    
    if (itemType === 'pdf') {
      // For PDFs, use the direct download
      onCustomAction(item.product_id, item.id);
    } else if (itemType === 'video' || itemType === 'audio') {
      // Create URL-friendly slugs
      const productSlug = createSlug(item.product_name || '');
      const itemSlug = createSlug(item.name || '');
      
      // Store the IDs in localStorage or sessionStorage for retrieval
      sessionStorage.setItem('currentProductId', item.product_id);
      sessionStorage.setItem('currentItemId', item.id);
      
      // Navigate to the slug-only URL pattern
      if (productSlug && itemSlug) {
        navigate(`/contenuti/${productSlug}/${itemSlug}`);
      } else {
        // Fallback if slugs can't be created
        navigate(`/contenuti/view/${item.product_id}/${item.id}`);
      }
    } else {
      // For other types, fallback to the custom action
      onCustomAction(item.product_id, item.id);
    }
  };
  
  // Render appropriate content based on item type and content
  const renderContent = () => {
    if (!item.content) {
      // If no content object, show the description directly
      return (
        <div className="mt-4">
          {item.description && (
            <div className="text-gray-600 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
              {showFullDescription ? item.description : truncateText(item.description)}
              {item.description && item.description.length > 150 && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullDescription(!showFullDescription);
                  }}
                  className="ml-2 text-green-600 font-medium hover:underline"
                >
                  {showFullDescription ? 'Mostra meno' : 'Leggi tutto'}
                </button>
              )}
            </div>
          )}
        </div>
      );
    }
    
    try {
      // If content is stored as a string, parse it
      const contentData = typeof item.content === 'string' 
        ? JSON.parse(item.content) 
        : item.content;
      
      // Different rendering based on type
      switch(item.type?.toLowerCase()) {
        case 'video':
          return (
            <div className="mt-4">
              {contentData.url && (
                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm flex items-center justify-center">
                  <div className="text-center p-3 sm:p-4">
                    <Video size={36} className="text-green-600 mx-auto mb-2" />
                    <p className="text-gray-600 font-medium text-sm sm:text-base">Clicca su "Riproduci" per visualizzare il video</p>
                  </div>
                </div>
              )}
              {contentData.description && (
                <div className="mt-3 text-gray-600 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
                  {showFullDescription ? contentData.description : truncateText(contentData.description)}
                  {contentData.description && contentData.description.length > 150 && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullDescription(!showFullDescription);
                      }}
                      className="ml-2 text-green-600 font-medium hover:underline"
                    >
                      {showFullDescription ? 'Mostra meno' : 'Leggi tutto'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        
        case 'pdf':
          return (
            <div className="mt-4">
              {contentData.url && (
                <div className="rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm flex items-center justify-center py-6 sm:py-8">
                  <div className="text-center p-3 sm:p-4">
                    <FileText size={36} className="text-green-600 mx-auto mb-2" />
                    <p className="text-gray-600 font-medium text-sm sm:text-base">Clicca su "Scarica" per ottenere il PDF</p>
                  </div>
                </div>
              )}
              {contentData.description && (
                <div className="mt-3 text-gray-600 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
                  {showFullDescription ? contentData.description : truncateText(contentData.description)}
                  {contentData.description && contentData.description.length > 150 && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullDescription(!showFullDescription);
                      }}
                      className="ml-2 text-green-600 font-medium hover:underline"
                    >
                      {showFullDescription ? 'Mostra meno' : 'Leggi tutto'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
          
        case 'audio':
          return (
            <div className="mt-4">
              {contentData.url && (
                <div className="rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm flex items-center justify-center py-6 sm:py-8">
                  <div className="text-center p-3 sm:p-4">
                    <Music size={36} className="text-green-600 mx-auto mb-2" />
                    <p className="text-gray-600 font-medium text-sm sm:text-base">Clicca su "Riproduci" per ascoltare l'audio</p>
                  </div>
                </div>
              )}
              {contentData.description && (
                <div className="mt-3 text-gray-600 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
                  {showFullDescription ? contentData.description : truncateText(contentData.description)}
                  {contentData.description && contentData.description.length > 150 && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullDescription(!showFullDescription);
                      }}
                      className="ml-2 text-green-600 font-medium hover:underline"
                    >
                      {showFullDescription ? 'Mostra meno' : 'Leggi tutto'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        
        // For other content types
        default:
          return (
            <div className="mt-4">
              {contentData.description && (
                <div className="text-gray-600 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
                  {showFullDescription ? contentData.description : truncateText(contentData.description)}
                  {contentData.description && contentData.description.length > 150 && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullDescription(!showFullDescription);
                      }}
                      className="ml-2 text-green-600 font-medium hover:underline"
                    >
                      {showFullDescription ? 'Mostra meno' : 'Leggi tutto'}
                    </button>
                  )}
                </div>
              )}
              {contentData.url && (
                <a 
                  href={contentData.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center text-green-600 hover:text-green-700 transition-all mt-3 py-2 px-4 sm:py-3 sm:px-5 bg-green-50 rounded-full border border-green-100 shadow-sm hover:shadow-md text-sm sm:text-base"
                >
                  Accedi al Contenuto
                  <ExternalLink size={14} className="ml-2" />
                </a>
              )}
            </div>
          );
      }
    } catch (error) {
      console.error("Error parsing content:", error);
      return (
        <div className="mt-4 p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl text-red-500 flex items-center">
          <AlertCircle size={16} className="mr-2" />
          <span className="text-sm sm:text-base">Errore nella visualizzazione del contenuto</span>
        </div>
      );
    }
  };

  // Get button icon and text based on item type
  const getButtonConfig = () => {
    const type = item.type?.toLowerCase();
    if (type === 'pdf') {
      return {
        icon: <FileDown size={16} className="mr-1 sm:mr-2 sm:size-5" />,
        text: 'Scarica',
        classes: 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'
      };
    } else if (type === 'video' || type === 'audio') {
      return {
        icon: <Eye size={16} className="mr-1 sm:mr-2 sm:size-5" />,
        text: 'Riproduci',
        classes: 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
      };
    } else {
      return {
        icon: <Eye size={16} className="mr-1 sm:mr-2 sm:size-5" />,
        text: 'Visualizza',
        classes: 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
      };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl p-3 sm:p-5 mb-4 cursor-pointer shadow-sm hover:shadow-md transition-all ${
        isOpen ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-green-100 hover:bg-green-50/30'
      }`}
      onClick={onClick}
    >
      {/* Header section - adapts to smaller screens */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        {/* Title and icon section */}
        <div className="flex items-start sm:items-center mb-3 sm:mb-0">
          <div className="mr-3 text-green-600 bg-white p-2 sm:p-3 rounded-xl border border-green-100 shadow-sm">
            {getIcon(item.type)}
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 text-base sm:text-lg">{item.name || 'Contenuto senza titolo'}</h3>
            {!isOpen && item.description && (
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                {truncateText(item.description, 60)}
                {item.description.length > 60 && <span className="text-green-600 ml-1">...</span>}
              </p>
            )}
          </div>
          {/* Small screen toggle icon */}
          <div className="sm:hidden ml-auto text-green-600 p-2 rounded-full border border-green-100">
            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        </div>
        
        {/* Action buttons - stacked on mobile, side by side on larger screens */}
        <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto">
          {/* Action button */}
          <button
            onClick={handleItemAction}
            className={`flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full transition-all mr-2 text-xs sm:text-sm ${buttonConfig.classes}`}
            aria-label={buttonConfig.text}
            title={buttonConfig.text}
          >
            {buttonConfig.icon}
            <span className="font-medium">{buttonConfig.text}</span>
          </button>
          
          {/* Toggle button - hidden on mobile (shown above) */}
          <div className="hidden sm:block text-green-600 p-2 rounded-full border border-green-100">
            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        </div>
      </div>
      
      {/* Expandable content section */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ContentItemCard;